import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Listbox, Tab } from '@headlessui/react';
import {
  ChevronUpDownIcon,
  CheckIcon,
  ShieldExclamationIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { exceptionTrackingApi } from '../lib/api';
import FeatureGate from '../components/FeatureGate';
import PermissionGate from '../components/PermissionGate';
import clsx from 'clsx';
import { PageHeader, StatusBadge, LoadingSpinner, EmptyState } from '../components/ui';
import { useTeamId } from '../hooks/useTeamId';

const EXCEPTION_TYPES = [
  { value: 'RATE_OVERRIDE', label: 'Rate Override' },
  { value: 'DTI_OVERRIDE', label: 'DTI Override' },
  { value: 'LTV_OVERRIDE', label: 'LTV Override' },
  { value: 'FEE_WAIVER', label: 'Fee Waiver' },
  { value: 'CREDIT_OVERRIDE', label: 'Credit Override' },
  { value: 'COLLATERAL_OVERRIDE', label: 'Collateral Override' },
  { value: 'DOCUMENTATION_WAIVER', label: 'Documentation Waiver' },
  { value: 'TERM_OVERRIDE', label: 'Term Override' },
  { value: 'OTHER', label: 'Other' },
];

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  PENDING_EXCEPTION: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
  APPROVED_EXCEPTION: { label: 'Approved', className: 'bg-green-100 text-green-700' },
  DENIED_EXCEPTION: { label: 'Denied', className: 'bg-red-100 text-red-700' },
  EXPIRED_EXCEPTION: { label: 'Expired', className: 'bg-gray-100 text-gray-500' },
};

const STATUS_FILTERS = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING_EXCEPTION', label: 'Pending' },
  { value: 'APPROVED_EXCEPTION', label: 'Approved' },
  { value: 'DENIED_EXCEPTION', label: 'Denied' },
  { value: 'EXPIRED_EXCEPTION', label: 'Expired' },
];

const TYPE_FILTERS = [
  { value: '', label: 'All Types' },
  ...EXCEPTION_TYPES,
];

export default function ExceptionTrackingPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [approveId, setApproveId] = useState<string | null>(null);
  const [denyId, setDenyId] = useState<string | null>(null);
  const [denyReason, setDenyReason] = useState('');
  const [approveNote, setApproveNote] = useState('');
  const limit = 25;

  // Team
  const { teamId } = useTeamId();

  // Exception list
  const { data: listData, isLoading } = useQuery({
    queryKey: ['exceptions', teamId, statusFilter, typeFilter, search, page],
    queryFn: () => exceptionTrackingApi.list(teamId!, {
      status: statusFilter || undefined,
      type: typeFilter || undefined,
      search: search || undefined,
      limit,
      offset: page * limit,
    }),
    enabled: !!teamId,
  });
  const exceptions = listData?.data?.exceptions || [];
  const total = listData?.data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  // Dashboard
  const { data: dashData } = useQuery({
    queryKey: ['exceptionsDashboard', teamId],
    queryFn: () => exceptionTrackingApi.getDashboard(teamId!),
    enabled: !!teamId,
  });
  const dash = dashData?.data;

  // LO Rates
  const { data: loRatesData } = useQuery({
    queryKey: ['exceptionsLORates', teamId],
    queryFn: () => exceptionTrackingApi.getLORates(teamId!),
    enabled: !!teamId,
  });

  // Disparities
  const { data: disparityData } = useQuery({
    queryKey: ['exceptionsDisparities', teamId],
    queryFn: () => exceptionTrackingApi.getDisparities(teamId!),
    enabled: !!teamId,
  });

  // Trends
  const { data: trendsData } = useQuery({
    queryKey: ['exceptionsTrends', teamId],
    queryFn: () => exceptionTrackingApi.getTrends(teamId!, { months: 12 }),
    enabled: !!teamId,
  });

  // Mutations
  const approveMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      exceptionTrackingApi.approve(teamId!, id, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exceptions'] });
      queryClient.invalidateQueries({ queryKey: ['exceptionsDashboard'] });
      toast.success('Exception approved');
      setApproveId(null);
      setApproveNote('');
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to approve'),
  });

  const denyMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      exceptionTrackingApi.deny(teamId!, id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exceptions'] });
      queryClient.invalidateQueries({ queryKey: ['exceptionsDashboard'] });
      toast.success('Exception denied');
      setDenyId(null);
      setDenyReason('');
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to deny'),
  });

  const handleExportCsv = async () => {
    if (!teamId) return;
    try {
      const res = await exceptionTrackingApi.exportCsv(teamId, {
        status: statusFilter || undefined,
        type: typeFilter || undefined,
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'exceptions.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  // Disparity alert count
  const disparities = disparityData?.data;
  const highRiskCount = [
    ...(disparities?.byRace || []),
    ...(disparities?.byEthnicity || []),
    ...(disparities?.bySex || []),
  ].filter((d: any) => d.riskLevel === 'HIGH' || d.riskLevel === 'MEDIUM').length;

  return (
    <FeatureGate feature="exception_tracking" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Exception Tracking"
          description="Track loan officer policy exceptions and monitor for fair lending disparities"
          actions={
            <div className="flex gap-2">
              <button
                onClick={handleExportCsv}
                className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Export CSV
              </button>
              <PermissionGate teamId={teamId} permission="compliance.write">
                <button
                  onClick={() => setShowCreate(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700"
                >
                  <PlusIcon className="h-4 w-4" />
                  Log Exception
                </button>
              </PermissionGate>
            </div>
          }
        />

        {/* Summary cards */}
        {dash && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-lg border bg-white p-4">
              <p className="text-xs font-medium text-gray-500">Total Exceptions</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{dash.totalExceptions}</p>
            </div>
            <div className="rounded-lg border bg-white p-4">
              <p className="text-xs font-medium text-gray-500">Pending Approval</p>
              <p className="mt-1 text-2xl font-bold text-yellow-600">{dash.pendingApproval}</p>
            </div>
            <div className="rounded-lg border bg-white p-4">
              <p className="text-xs font-medium text-gray-500">This Month</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{dash.thisMonth}</p>
              {dash.lastMonth > 0 && (
                <p className="mt-0.5 text-xs text-gray-400">vs {dash.lastMonth} last month</p>
              )}
            </div>
            <div className="rounded-lg border bg-white p-4">
              <p className="text-xs font-medium text-gray-500">Disparity Alerts</p>
              <p className={clsx('mt-1 text-2xl font-bold', highRiskCount > 0 ? 'text-red-600' : 'text-green-600')}>
                {highRiskCount}
              </p>
            </div>
          </div>
        )}

        {/* Tabs: List / Analytics */}
        <Tab.Group>
          <Tab.List className="flex gap-4 border-b">
            <Tab className={({ selected }) => clsx('border-b-2 px-1 py-2 text-sm font-medium', selected ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              Exceptions
            </Tab>
            <Tab className={({ selected }) => clsx('border-b-2 px-1 py-2 text-sm font-medium', selected ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              Analytics
            </Tab>
          </Tab.List>

          <Tab.Panels>
            {/* Exceptions List Tab */}
            <Tab.Panel className="space-y-4 pt-4">
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3">
                <Listbox value={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(0); }}>
                  <div className="relative w-40">
                    <Listbox.Button className="relative w-full cursor-pointer rounded-lg border bg-white py-2 pl-3 pr-10 text-left text-sm">
                      <span>{STATUS_FILTERS.find(f => f.value === statusFilter)?.label}</span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                      </span>
                    </Listbox.Button>
                    <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                        {STATUS_FILTERS.map(f => (
                          <Listbox.Option key={f.value} value={f.value}
                            className={({ active }) => clsx('cursor-pointer select-none py-2 pl-10 pr-4 text-sm', active ? 'bg-blue-50 text-blue-700' : 'text-gray-900')}>
                            {({ selected }) => (
                              <>
                                <span className={clsx('block truncate', selected && 'font-medium')}>{f.label}</span>
                                {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"><CheckIcon className="h-4 w-4" /></span>}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>

                <Listbox value={typeFilter} onChange={(v) => { setTypeFilter(v); setPage(0); }}>
                  <div className="relative w-48">
                    <Listbox.Button className="relative w-full cursor-pointer rounded-lg border bg-white py-2 pl-3 pr-10 text-left text-sm">
                      <span>{TYPE_FILTERS.find(f => f.value === typeFilter)?.label}</span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                      </span>
                    </Listbox.Button>
                    <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                        {TYPE_FILTERS.map(f => (
                          <Listbox.Option key={f.value} value={f.value}
                            className={({ active }) => clsx('cursor-pointer select-none py-2 pl-10 pr-4 text-sm', active ? 'bg-blue-50 text-blue-700' : 'text-gray-900')}>
                            {({ selected }) => (
                              <>
                                <span className={clsx('block truncate', selected && 'font-medium')}>{f.label}</span>
                                {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600"><CheckIcon className="h-4 w-4" /></span>}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>

                <input
                  type="text"
                  placeholder="Search LO name or loan #..."
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(0); }}
                  className="rounded-lg border border-gray-300 py-2 px-3 text-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              {/* Table */}
              <div className="rounded-lg border bg-white">
                {isLoading ? (
                  <LoadingSpinner size="md" className="py-12" />
                ) : exceptions.length === 0 ? (
                  <EmptyState
                    icon={ShieldExclamationIcon}
                    title="No exceptions found"
                    description="Log your first exception to start tracking."
                  />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">LO Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Loan #</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Deviation</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {exceptions.map((ex: any) => {
                          const st = STATUS_CONFIG[ex.status] || STATUS_CONFIG.PENDING_EXCEPTION;
                          const typeLabel = EXCEPTION_TYPES.find(t => t.value === ex.exceptionType)?.label || ex.exceptionType;
                          return (
                            <tr key={ex.id} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                                {new Date(ex.exceptionDate).toLocaleDateString()}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                                {ex.loanOfficerName}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3">
                                <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                                  {typeLabel}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                                {ex.loanNumber || '—'}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                                {ex.deviationAmount || '—'}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3">
                                <StatusBadge variant={
                                  ex.status === 'APPROVED_EXCEPTION' ? 'success' :
                                  ex.status === 'PENDING_EXCEPTION' ? 'warning' :
                                  ex.status === 'DENIED_EXCEPTION' ? 'danger' :
                                  'neutral'
                                }>
                                  {st.label}
                                </StatusBadge>
                              </td>
                              <td className="whitespace-nowrap px-4 py-3">
                                {ex.status === 'PENDING_EXCEPTION' && (
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => setApproveId(ex.id)}
                                      className="text-green-600 hover:text-green-700"
                                      title="Approve"
                                    >
                                      <CheckCircleIcon className="h-5 w-5" />
                                    </button>
                                    <button
                                      onClick={() => setDenyId(ex.id)}
                                      className="text-red-500 hover:text-red-600"
                                      title="Deny"
                                    >
                                      <XCircleIcon className="h-5 w-5" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between border-t px-4 py-3">
                    <p className="text-sm text-gray-500">
                      Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
                        className="rounded border px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">Previous</button>
                      <button onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}
                        className="rounded border px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">Next</button>
                    </div>
                  </div>
                )}
              </div>
            </Tab.Panel>

            {/* Analytics Tab */}
            <Tab.Panel className="space-y-6 pt-4">
              {/* LO Grant Rates */}
              <div className="rounded-lg border bg-white">
                <div className="border-b px-4 py-3">
                  <h3 className="text-sm font-semibold text-gray-900">Loan Officer Grant Rates</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">LO Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Total</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Approved</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Denied</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Top Types</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {(loRatesData?.data || []).map((lo: any) => (
                        <tr key={lo.loanOfficerId} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{lo.loanOfficerName}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{lo.totalExceptions}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-green-600">{lo.approvedCount}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-red-600">{lo.deniedCount}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {Object.entries(lo.byType || {}).sort(([, a]: any, [, b]: any) => b - a).slice(0, 2).map(([type, count]: any) => (
                              <span key={type} className="mr-2 inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-xs">
                                {EXCEPTION_TYPES.find(t => t.value === type)?.label || type}: {count}
                              </span>
                            ))}
                          </td>
                        </tr>
                      ))}
                      {(!loRatesData?.data || loRatesData.data.length === 0) && (
                        <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-400">No data yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Demographic Disparities */}
              {disparities && (disparities.byRace?.length > 0 || disparities.byEthnicity?.length > 0 || disparities.bySex?.length > 0) && (
                <div className="rounded-lg border bg-white">
                  <div className="border-b px-4 py-3">
                    <h3 className="text-sm font-semibold text-gray-900">Demographic Disparity Analysis</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Ratios compare exception rates vs control group (White / Non-Hispanic / Male)</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Dimension</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Group</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Exceptions</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Ratio</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Risk</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {[
                          ...(disparities.byRace || []).map((d: any) => ({ ...d, dimension: 'Race' })),
                          ...(disparities.byEthnicity || []).map((d: any) => ({ ...d, dimension: 'Ethnicity' })),
                          ...(disparities.bySex || []).map((d: any) => ({ ...d, dimension: 'Sex' })),
                        ].map((d: any, i: number) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{d.dimension}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{d.group}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{d.exceptionCount}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{d.ratio}x</td>
                            <td className="whitespace-nowrap px-4 py-3">
                              <StatusBadge variant={
                                d.riskLevel === 'HIGH' ? 'danger' :
                                d.riskLevel === 'MEDIUM' ? 'warning' :
                                'success'
                              }>
                                {d.riskLevel}
                              </StatusBadge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Monthly Trends */}
              {trendsData?.data && trendsData.data.length > 0 && (
                <div className="rounded-lg border bg-white">
                  <div className="border-b px-4 py-3">
                    <h3 className="text-sm font-semibold text-gray-900">Monthly Exception Trends</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Month</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Total</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Approved</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Denied</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {trendsData.data.map((t: any) => (
                          <tr key={t.month} className="hover:bg-gray-50">
                            <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{t.month}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{t.count}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-green-600">{t.approved}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-red-600">{t.denied}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>

        {/* Create Exception Modal */}
        <CreateExceptionModal
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          teamId={teamId}
        />

        {/* Approve Modal */}
        <Transition appear show={!!approveId} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setApproveId(null)}>
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 flex items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">Approve Exception</Dialog.Title>
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700">Note (optional)</label>
                    <textarea value={approveNote} onChange={e => setApproveNote(e.target.value)}
                      rows={3} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button onClick={() => setApproveId(null)} className="rounded-lg border px-3 py-2 text-sm">Cancel</button>
                    <button onClick={() => approveId && approveMutation.mutate({ id: approveId, note: approveNote || undefined })}
                      className="rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700">Approve</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition>

        {/* Deny Modal */}
        <Transition appear show={!!denyId} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setDenyId(null)}>
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 flex items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">Deny Exception</Dialog.Title>
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700">Reason (required)</label>
                    <textarea value={denyReason} onChange={e => setDenyReason(e.target.value)}
                      rows={3} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Enter denial reason..." />
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button onClick={() => setDenyId(null)} className="rounded-lg border px-3 py-2 text-sm">Cancel</button>
                    <button onClick={() => denyId && denyReason && denyMutation.mutate({ id: denyId, reason: denyReason })}
                      disabled={!denyReason}
                      className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">Deny</button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition>
      </div>
    </FeatureGate>
  );
}

// ─── Create Exception Modal ──────────────────────────────────────────────────

function CreateExceptionModal({ isOpen, onClose, teamId }: { isOpen: boolean; onClose: () => void; teamId?: string }) {
  const queryClient = useQueryClient();
  const [showDemographics, setShowDemographics] = useState(false);
  const [form, setForm] = useState({
    exceptionType: 'RATE_OVERRIDE',
    loanOfficerId: '',
    loanOfficerName: '',
    justification: '',
    loanNumber: '',
    loanAmount: '',
    policyField: '',
    policyThreshold: '',
    actualValue: '',
    deviationAmount: '',
    applicantRace1: '',
    applicantEthnicity1: '',
    applicantSex: '',
    applicantAge: '',
    censusTract: '',
    county: '',
    state: '',
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => exceptionTrackingApi.create(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exceptions'] });
      queryClient.invalidateQueries({ queryKey: ['exceptionsDashboard'] });
      toast.success('Exception logged');
      onClose();
      setForm({
        exceptionType: 'RATE_OVERRIDE', loanOfficerId: '', loanOfficerName: '', justification: '',
        loanNumber: '', loanAmount: '', policyField: '', policyThreshold: '', actualValue: '',
        deviationAmount: '', applicantRace1: '', applicantEthnicity1: '', applicantSex: '',
        applicantAge: '', censusTract: '', county: '', state: '',
      });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to create'),
  });

  const handleSubmit = () => {
    if (!form.loanOfficerName || !form.justification) {
      toast.error('LO name and justification are required');
      return;
    }
    createMutation.mutate({
      ...form,
      loanOfficerId: form.loanOfficerId || form.loanOfficerName.toLowerCase().replace(/\s+/g, '-'),
      loanAmount: form.loanAmount ? parseFloat(form.loanAmount) : null,
      applicantRace1: form.applicantRace1 || null,
      applicantEthnicity1: form.applicantEthnicity1 || null,
      applicantSex: form.applicantSex || null,
      applicantAge: form.applicantAge || null,
      censusTract: form.censusTract || null,
      county: form.county || null,
      state: form.state || null,
    });
  };

  const set = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
                <div className="flex items-center justify-between">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">Log Exception</Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
                </div>

                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Exception Type</label>
                    <select value={form.exceptionType} onChange={e => set('exceptionType', e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                      {EXCEPTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">LO Name *</label>
                      <input value={form.loanOfficerName} onChange={e => set('loanOfficerName', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">LO ID</label>
                      <input value={form.loanOfficerId} onChange={e => set('loanOfficerId', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Auto-generated if blank" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Justification *</label>
                    <textarea value={form.justification} onChange={e => set('justification', e.target.value)}
                      rows={3} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Loan #</label>
                      <input value={form.loanNumber} onChange={e => set('loanNumber', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Loan Amount</label>
                      <input type="number" value={form.loanAmount} onChange={e => set('loanAmount', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Policy Threshold</label>
                      <input value={form.policyThreshold} onChange={e => set('policyThreshold', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="e.g., 5.25%" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Actual Value</label>
                      <input value={form.actualValue} onChange={e => set('actualValue', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="e.g., 4.75%" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Deviation</label>
                    <input value={form.deviationAmount} onChange={e => set('deviationAmount', e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="e.g., -0.50%" />
                  </div>

                  {/* Demographics toggle */}
                  <button onClick={() => setShowDemographics(!showDemographics)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700">
                    {showDemographics ? '▾ Hide Demographics' : '▸ Add Demographics (HMDA)'}
                  </button>

                  {showDemographics && (
                    <div className="space-y-3 rounded-lg border border-gray-200 p-3">
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Race</label>
                          <select value={form.applicantRace1} onChange={e => set('applicantRace1', e.target.value)}
                            className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-xs">
                            <option value="">Select</option>
                            <option value="1">AI/AN</option>
                            <option value="2">Asian</option>
                            <option value="3">Black</option>
                            <option value="4">NH/PI</option>
                            <option value="5">White</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Ethnicity</label>
                          <select value={form.applicantEthnicity1} onChange={e => set('applicantEthnicity1', e.target.value)}
                            className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-xs">
                            <option value="">Select</option>
                            <option value="1">Hispanic/Latino</option>
                            <option value="2">Not Hispanic/Latino</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Sex</label>
                          <select value={form.applicantSex} onChange={e => set('applicantSex', e.target.value)}
                            className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-xs">
                            <option value="">Select</option>
                            <option value="1">Male</option>
                            <option value="2">Female</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Census Tract</label>
                          <input value={form.censusTract} onChange={e => set('censusTract', e.target.value)}
                            className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-xs" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">County</label>
                          <input value={form.county} onChange={e => set('county', e.target.value)}
                            className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-xs" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">State</label>
                          <input value={form.state} onChange={e => set('state', e.target.value)}
                            className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-xs" maxLength={2} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-5 flex justify-end gap-2">
                  <button onClick={onClose} className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700">Cancel</button>
                  <button onClick={handleSubmit} disabled={createMutation.isPending}
                    className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50">
                    {createMutation.isPending ? 'Saving...' : 'Log Exception'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
